import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWandMagicSparkles,
  faPlay,
  faCircleNotch,
  faUpload, // <-- Se usará
} from '@fortawesome/free-solid-svg-icons';
import FileUpload from '../../../../components/common/FileUpload';
import MediaPreview from '../../../../components/common/MediaPreview';

// --- COMPONENTE DE PROGRESO CORREGIDO ---
const UploadProgressIndicator = ({
  uploadStatus, // <-- Se añade prop
  uploadStatusMessage,
  uploadProgress,
  UPLOAD_STATE, // <-- Se añade prop
}) => {
  const isUploading = uploadStatus === UPLOAD_STATE.UPLOADING_FILES;
  const icon = isUploading ? faUpload : faCircleNotch;

  return (
    <div className="sticky top-0 bg-white dark:bg-neutral-800 z-40 p-4 rounded-lg shadow-md border border-neutral-200 dark:border-neutral-700 my-4">
      <div className="flex items-center gap-3 text-lg font-semibold text-neutral-800 dark:text-neutral-200">
        <FontAwesomeIcon icon={icon} spin={!isUploading} />
        <span>{uploadStatusMessage}</span>
      </div>
      <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2.5 mt-3">
        <div
          className="bg-primary h-2.5 rounded-full transition-all duration-300"
          style={{ width: `${uploadProgress}%` }}
        ></div>
      </div>
    </div>
  );
};
// ------------------------------------

const PackFileUploadManager = ({
  items,
  isPremium,
  onAddFiles,
  onRemoveFile,
  // onSetContentCover, <-- PROP ELIMINADA (OBSOLETA)
  onEditItemEffect,
  isUserView,
  isPending,
  uploadStatus,
  uploadProgress,
  uploadStatusMessage,
  UPLOAD_STATE,
}) => {
  const { t } = useTranslation();
  const [previewUrls, setPreviewUrls] = useState({});

  // (useEffect para previewUrls - sin cambios)
  useEffect(() => {
    const newUrls = {};
    items.forEach((item) => {
      if (isUserView && item.effectPreviewUrl) {
        newUrls[item.id] = item.effectPreviewUrl;
      } else {
        const fileToPreview = item.thumbnailFile || item.file;
        if (fileToPreview) {
          newUrls[item.id] = URL.createObjectURL(fileToPreview);
        }
      }
    });
    setPreviewUrls((prevUrls) => {
      Object.keys(prevUrls).forEach((id) => {
        if (!newUrls[id]) {
          URL.revokeObjectURL(prevUrls[id]);
        }
      });
      return newUrls;
    });
    return () => {
      Object.values(newUrls).forEach((url) => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [items, isUserView]);

  return (
    <div>
      <label className="block font-semibold mb-2 text-neutral-800 dark:text-neutral-200">
        {t('packs.create_modal.file_upload.title')}
        <span className="text-red-500">*</span>
      </label>
      <FileUpload
        onFileSelect={onAddFiles}
        accept="image/*,video/mp4,video/quicktime,video/x-matroska,video/webm"
        multiple={true}
        disabled={isPending}
        label={t('packs.create_modal.file_upload.select_files_button')}
      />
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
        {t('packs.create_modal.file_upload.help_text')}
      </p>
      {isPremium && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
          {t('packs.create_modal.file_upload.default_effect_note')}
        </p>
      )}

      {/* --- INDICADOR DE PROGRESO CORREGIDO --- */}
      {isPending && uploadStatus !== UPLOAD_STATE.CREATING_DRAFT && (
        <UploadProgressIndicator
          uploadStatus={uploadStatus}
          uploadStatusMessage={uploadStatusMessage}
          uploadProgress={uploadProgress}
          UPLOAD_STATE={UPLOAD_STATE}
        />
      )}
      {/* ------------------------------------------- */}

      {items.length > 0 && (
        <div className="mt-4">
          <h4 className="font-semibold text-neutral-800 dark:text-neutral-200">
            {t('packs.create_modal.file_upload.selected_files_title', {
              count: items.length,
            })}
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2 border-t border-neutral-200 dark:border-neutral-700 pt-3">
            {items.map((item, index) => {
              const isVideo = item.file.type.startsWith('video/');
              const itemPreview = {
                url: previewUrls[item.id] || '',
                type: isVideo ? 'video' : 'image',
              };

              return (
                <MediaPreview
                  key={item.id}
                  preview={itemPreview}
                  index={index}
                  onRemove={() => onRemoveFile(item.id)}
                  disabled={isPending}
                  isCover={false}
                  onSetCover={() => {}} // Lógica obsoleta en este componente
                  isPremium={isPremium}
                  onEditEffect={() => onEditItemEffect(item)}
                  isUserView={isUserView}
                  effect={item.effect}
                >
                  {isVideo && !isUserView && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                      <span className="bg-black/50 text-white rounded-full h-8 w-8 flex items-center justify-center">
                        <FontAwesomeIcon
                          icon={faPlay}
                          className="text-sm ml-0.5"
                        />
                      </span>
                    </div>
                  )}
                  {isPremium && item.effect && !isUserView && (
                    <div className="absolute bottom-1 left-1.5 bg-primary text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-lg flex items-center gap-1 z-10 pointer-events-none">
                      <FontAwesomeIcon icon={faWandMagicSparkles} size="xs" />
                      <span>
                        {t(
                          'packs.create_modal.file_upload.effect_applied_badge'
                        )}
                      </span>
                    </div>
                  )}
                </MediaPreview>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PackFileUploadManager;
